import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { trackMixpanelPageView } from '@helpers/post';
import { useQueryParamCapture } from '@helpers/useQueryParamCapture';
import AdvancedOptionCard from '../OptionCards/AdvancedOptionCard';
import ExpandableSection from '../ExpandableSection';
import ProcessSteps from './ProcessSteps';
import './NextSteps.scss';

// Step five of the updated Job Poster funnel (GO4-1728). The progress stepper (4 of 5) is
// rendered centered in the top navigation — see scenes/V3/CreateAccountScene/funnelSteps.
const READY_TO_POST_PATH = '/create-account/persona';
const DASHBOARD_PATH = '/dashboard';
const CONSULTATION_FORM_URL =
  'https://www.go4.io/contract-services-consultation-form-platform/';

const COVERAGE_CARDS = [
  {
    title: 'Event-by-event coverage',
    body: 'Post individual shifts for games, practices, tournaments, or camps. ATs apply and you choose.',
    bestFor: 'Club sports, tournaments, camps, facilities',
  },
  {
    title: 'Seasonal or full-time',
    body: 'Go4 matches you with an AT for an extended engagement — season, school year, or contract.',
    bestFor: 'Schools, colleges, industrial, clinics',
  },
];

const COST_ROWS = [
  ['Posting a job', 'Free'],
  ['Per diem coverage', 'You set the rate'],
  ['Long-term placement', 'Custom quote'],
  ['EAP Generator', 'Included'],
  ['EMR Documentation', 'Included'],
  ['Insurance', 'Included'],
];

const PROCESS_STEPS = [
  { title: 'EAP Generator', description: 'Create venue-specific Emergency Action Plans in minutes. Required for compliance at most schools and facilities.' },
  { title: 'Post a Job', description: 'Describe the shift — setting, location, date, time, pay.' },
  { title: 'ATs Apply', description: 'Credentialed ATs see your post and apply.' },
  { title: 'Review & Hire', description: 'Browse profiles, credentials, ratings. Confirm.' },
  { title: 'Coverage Day', description: 'Your AT arrives prepared. EMR handles documentation.' },
  { title: 'Payment', description: 'Pay through Go4. Rate and review your AT.' },
];

const LONG_TERM_STEPS = [
  { title: 'Assessment', description: 'Your specific needs are used to create a unique job opportunity that is shared with over 26k ATs.' },
  { title: 'Placement', description: 'Go4 will use your initial assessment to screen potential candidates to set up a final interview with your team.' },
  { title: 'On-Going Support', description: 'After placement, Go4 helps your AT assess and update your safety initiatives including your Emergency Action Plan.' },
];

const NextSteps = props => {
  const { user } = props;
  // Top-level content sections behave as a singular accordion (one open at a time).
  const [openSection, setOpenSection] = useState(null);

  // Captures and persists the `mwr` referral param (whitelisted route).
  useQueryParamCapture();

  useEffect(() => {
    trackMixpanelPageView('/create-account/ready-to-post', user);
  }, [user]);

  const firstName = get(user, 'firstName') || (get(user, 'name') || '').split(' ')[0];

  const toggleSection = index => () =>
    setOpenSection(prev => (prev === index ? null : index));

  const onReadyToPost = () => {
    trackMixpanelPageView('/create-account/ready-to-post/post-first-job', user);
    props.history.push(READY_TO_POST_PATH);
  };

  const onJustBrowsing = () => {
    trackMixpanelPageView('/create-account/ready-to-post/just-browsing', user);
    props.history.push(DASHBOARD_PATH);
  };

  const onScheduleConsultation = () => {
    trackMixpanelPageView('/create-account/ready-to-post/schedule-consultation', user);
  };

  return (
    <div className="v3NextSteps">
      <div className="next-steps-body">
        <header className="next-steps-header">
          <h1 className="next-steps-title">
            Welcome to Go4{firstName ? `, ${firstName}` : ''}.
          </h1>
          <p className="next-steps-subtitle">
            You&apos;ve just joined 5,000+ organizations that trust Go4 to put a credentialed
            athletic trainer on every sideline.
          </p>
        </header>

        <div className="next-steps-options">
          <AdvancedOptionCard
            className="next-steps-option next-steps-option--primary"
            title="Post Your First Job"
            body="I'm ready to find an AT — now or in the coming weeks."
            linkText="Get Started"
            onClick={onReadyToPost}
          />
          <AdvancedOptionCard
            className="next-steps-option next-steps-option--browsing"
            title="I'm Just Browsing"
            body="Take me to my dashboard — I'll explore at my own pace."
            linkText="Go to Dashboard"
            onClick={onJustBrowsing}
          />
        </div>

        <div className="next-steps-extras">
          {/* Section 1 — Per Diem vs. Long-Term */}
          <ExpandableSection
            title="Per Diem vs. Long-Term"
            subtitle="Which coverage model is right for your organization?"
            expanded={openSection === 0}
            onToggle={toggleSection(0)}
          >
            <h2 className="section-heading">Per Diem vs. Long-Term</h2>
            <div className="coverage-cards">
              {COVERAGE_CARDS.map(card => (
                <div className="coverage-card" key={card.title}>
                  <div className="coverage-card__box">
                    <p className="coverage-card__title">{card.title}</p>
                    <p className="coverage-card__body">{card.body}</p>
                  </div>
                  <div className="coverage-card__best-for">
                    <p>Best for:</p>
                    <p>{card.bestFor}</p>
                  </div>
                </div>
              ))}
            </div>
          </ExpandableSection>

          {/* Section 2 — Cost & What's Included */}
          <ExpandableSection
            title="Cost & What's Included"
            subtitle="Pricing, plus EAP Generator and EMR — at no extra cost."
            expanded={openSection === 1}
            onToggle={toggleSection(1)}
          >
            <h2 className="section-heading">Cost &amp; What&apos;s Included</h2>
            <table className="cost-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {COST_ROWS.map(([item, cost]) => (
                  <tr key={item}>
                    <td>{item}</td>
                    <td className="cost-table__cost">{cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="info-boxes">
              <div className="info-box">
                <p className="info-box__title">EAP Generator</p>
                <p className="info-box__body">
                  Create venue-specific Emergency Action Plans in minutes. Required for compliance
                  at most schools and facilities.
                </p>
              </div>
              <div className="info-box">
                <p className="info-box__title">EMR Documentation</p>
                <p className="info-box__body">
                  HIPAA-compliant electronic medical records for injury tracking, treatment logs,
                  and athlete health histories.
                </p>
              </div>
            </div>
          </ExpandableSection>

          {/* Section 3 — How the Process Works */}
          <ExpandableSection
            title="How the Process Works"
            subtitle="From posting to payment — the full job lifecycle."
            expanded={openSection === 2}
            onToggle={toggleSection(2)}
          >
            <h2 className="section-heading">How the Process Works</h2>
            <ProcessSteps steps={PROCESS_STEPS} />
            <div className="info-boxes">
              <div className="info-box">
                <p className="info-box__title">How long does it take?</p>
                <p className="info-box__body">
                  Most per diem jobs get their first applicant within 24-48 hours. Post 3-5 days in
                  advance for best selection.
                </p>
              </div>
            </div>
          </ExpandableSection>

          {/* Section 4 — Need Long-Term Staffing? (links to consultation, bypassing step 6) */}
          <ExpandableSection
            title="Need Long-Term Staffing?"
            subtitle="Talk to our team about seasonal or full-time placement."
            expanded={openSection === 3}
            onToggle={toggleSection(3)}
          >
            <h2 className="section-heading">How Long-Term Staffing Works</h2>
            <ProcessSteps steps={LONG_TERM_STEPS} />
            <div className="consultation-cta">
              <a
                className="consultation-cta__button"
                href={CONSULTATION_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onScheduleConsultation}
              >
                Schedule a Consultation
                <i className="fas fa-arrow-right" aria-hidden="true" />
              </a>
            </div>
          </ExpandableSection>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  user: state.session.currentUser,
});

export default connect(mapStateToProps)(NextSteps);
